/**
 * VendorAvatar — single avatar primitive for every vendor display surface.
 *
 * Resolves a usable image URL via getVendorAvatar() (which already filters
 * placeholder/example.com strings), and gracefully degrades to a colored
 * initial when the URL is null OR fails to load (HTTP 404, expired signed
 * URL, network block). Without onError handling, broken URLs render the
 * browser's default broken-image glyph — the regression we're closing.
 *
 * Props:
 *   - vendor: object with any subset of { profile_photo, owner_photo,
 *     vendor_image, vendor_name, business_name }. Pass an inquiry, a
 *     vendor_tracker row, a vendor record, or a headerVendor blob.
 *   - size: pixel size for both width/height. Default 40.
 *   - className: extra classes for the wrapping element (sizing,
 *     ring/border, margin, etc.).
 *   - testId: optional data-testid; the fallback span gets `${testId}-fallback`.
 */
import React, { useState, useEffect } from 'react';
import { getVendorAvatar } from '../utils/vendorImage';

export const VendorAvatar = ({
  vendor,
  size = 40,
  className = '',
  testId,
}) => {
  const resolved =
    getVendorAvatar(vendor) || (vendor && vendor.vendor_image) || null;
  const [failed, setFailed] = useState(false);

  // If the URL changes (e.g. a new inquiry is selected), reset the
  // failure flag so the new URL gets a fresh attempt.
  useEffect(() => {
    setFailed(false);
  }, [resolved]);

  const name =
    (vendor && (vendor.business_name || vendor.vendor_name)) || 'V';
  const initial = name.trim().charAt(0).toUpperCase() || 'V';

  const dimStyle = { width: size, height: size, minWidth: size };
  const fontSize = Math.max(10, Math.round(size * 0.4));

  if (resolved && !failed) {
    return (
      <img
        src={resolved}
        alt=""
        onError={() => setFailed(true)}
        className={`rounded-full object-cover flex-shrink-0 ring-1 ring-gray-200 ${className}`}
        style={dimStyle}
        data-testid={testId}
      />
    );
  }

  return (
    <span
      className={`rounded-full bg-emerald-100 text-emerald-700 font-semibold flex items-center justify-center flex-shrink-0 ${className}`}
      style={{ ...dimStyle, fontSize }}
      data-testid={testId ? `${testId}-fallback` : undefined}
    >
      {initial}
    </span>
  );
};

export default VendorAvatar;
